import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

const { FiDollarSign, FiTrendingUp, FiCalendar, FiX, FiPercent, FiInfo, FiClock, FiPieChart } = FiIcons;

function SWPCalculator({ onClose }) {
  const [formData, setFormData] = useState({
    // Desired outcome
    desiredMonthlyIncome: 100000,
    swpDuration: 15, // years

    // Investment period
    investmentPeriod: 5, // years of active investment
    gapPeriod: 10, // years between investment stop and SWP start

    // Financial assumptions
    accumulationReturn: 12, // return during investment phase
    growthReturn: 10, // return during gap period
    withdrawalReturn: 8, // return during SWP phase
    inflationRate: 6,

    // Additional expenses during SWP with time periods
    monthlyExpenses: 50000,
    monthlyExpensesStartYear: 0, // Start from beginning
    monthlyExpensesEndYear: 15, // Continue till end

    emiAmount: 0,
    emiStartYear: 0,
    emiEndYear: 15,

    schoolFees: 0, // annual
    schoolFeesStartYear: 0,
    schoolFeesEndYear: 15,

    houseHoldExpenses: 0, // annual
    houseHoldExpensesStartYear: 0,
    houseHoldExpensesEndYear: 15,

    // Others section with time period
    otherExpenseName: '',
    otherExpenses: 0, // annual
    otherExpensesStartYear: 0,
    otherExpensesEndYear: 15,
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Comprehensive calculations
  const calculations = useMemo(() => {
    const {
      desiredMonthlyIncome,
      swpDuration,
      investmentPeriod,
      gapPeriod,
      accumulationReturn,
      growthReturn,
      withdrawalReturn,
      inflationRate,
      monthlyExpenses,
      monthlyExpensesStartYear,
      monthlyExpensesEndYear,
      emiAmount,
      emiStartYear,
      emiEndYear,
      schoolFees,
      schoolFeesStartYear,
      schoolFeesEndYear,
      houseHoldExpenses,
      houseHoldExpensesStartYear,
      houseHoldExpensesEndYear,
      otherExpenses,
      otherExpensesStartYear,
      otherExpensesEndYear,
    } = formData;

    // Calculate total monthly requirement (inflation-adjusted to SWP start)
    const yearsToSWPStart = investmentPeriod + gapPeriod;
    const inflationMultiplier = Math.pow(1 + inflationRate / 100, yearsToSWPStart);

    const futureDesiredIncome = desiredMonthlyIncome * inflationMultiplier;

    // Generate year-by-year breakdown for SWP phase
    const yearlyBreakdown = [];
    let currentCorpus = 0;
    let phase = 'investment';
    let yearIndex = 0;

    // Investment phase
    for (let year = 1; year <= investmentPeriod; year++) {
      yearlyBreakdown.push({
        year: yearIndex + 1,
        age: yearIndex + 1,
        phase: 'Investment Phase',
        investment: 0, // Will be calculated later
        corpus: 0, // Will be calculated later
        withdrawal: 0,
        income: 0
      });
      yearIndex++;
    }

    // Gap/Growth phase
    for (let year = 1; year <= gapPeriod; year++) {
      yearlyBreakdown.push({
        year: yearIndex + 1,
        age: yearIndex + 1,
        phase: 'Growth Phase',
        investment: 0,
        corpus: 0, // Will be calculated later
        withdrawal: 0,
        income: 0
      });
      yearIndex++;
    }

    // SWP phase - Calculate year-wise requirements with time periods
    let totalAnnualRequirements = [];
    for (let year = 1; year <= swpDuration; year++) {
      const swpYear = yearsToSWPStart + year;
      const yearInflation = Math.pow(1 + inflationRate / 100, swpYear);

      // Calculate components based on time periods
      let yearlyMonthlyExpenses = 0;
      if (year >= monthlyExpensesStartYear && year <= monthlyExpensesEndYear) {
        yearlyMonthlyExpenses = monthlyExpenses * yearInflation * 12;
      }

      let yearlyEMI = 0;
      if (year >= emiStartYear && year <= emiEndYear) {
        yearlyEMI = emiAmount * yearInflation * 12;
      }

      let yearlySchoolFees = 0;
      if (year >= schoolFeesStartYear && year <= schoolFeesEndYear) {
        yearlySchoolFees = schoolFees * yearInflation;
      }

      let yearlyHouseHoldExpenses = 0;
      if (year >= houseHoldExpensesStartYear && year <= houseHoldExpensesEndYear) {
        yearlyHouseHoldExpenses = houseHoldExpenses * yearInflation;
      }

      let yearlyOtherExpenses = 0;
      if (year >= otherExpensesStartYear && year <= otherExpensesEndYear) {
        yearlyOtherExpenses = otherExpenses * yearInflation;
      }

      const yearlyDesiredIncome = futureDesiredIncome * Math.pow(1 + inflationRate / 100, year - 1) * 12;

      const totalYearlyRequirement = yearlyDesiredIncome + yearlyMonthlyExpenses + yearlyEMI + yearlySchoolFees + yearlyHouseHoldExpenses + yearlyOtherExpenses;

      totalAnnualRequirements.push(totalYearlyRequirement);
    }

    // Calculate corpus required at SWP start using year-by-year withdrawals
    const realReturnDuringSWP = ((1 + withdrawalReturn / 100) / (1 + inflationRate / 100)) - 1;
    let corpusRequiredAtSWPStart = 0;

    // Work backwards from last year to first year
    let remainingCorpusNeeded = 0;
    for (let year = swpDuration - 1; year >= 0; year--) {
      const yearlyRequirement = totalAnnualRequirements[year];
      remainingCorpusNeeded = (remainingCorpusNeeded + yearlyRequirement) / (1 + withdrawalReturn / 100);
    }
    corpusRequiredAtSWPStart = remainingCorpusNeeded;

    // Calculate corpus at end of investment period
    const corpusAtEndOfInvestment = corpusRequiredAtSWPStart / Math.pow(1 + growthReturn / 100, gapPeriod);

    // Calculate required monthly SIP during investment period
    const monthlyReturnDuringInvestment = accumulationReturn / 100 / 12;
    const investmentMonths = investmentPeriod * 12;
    let monthlySIP = 0;
    if (monthlyReturnDuringInvestment > 0) {
      monthlySIP = corpusAtEndOfInvestment / ((Math.pow(1 + monthlyReturnDuringInvestment, investmentMonths) - 1) / monthlyReturnDuringInvestment);
    } else {
      monthlySIP = corpusAtEndOfInvestment / investmentMonths;
    }

    // Calculate required lumpsum
    const lumpsumRequired = corpusAtEndOfInvestment / Math.pow(1 + accumulationReturn / 100, investmentPeriod);

    // Total investment amounts
    const totalSIPInvestment = monthlySIP * 12 * investmentPeriod;

    // Generate complete year-by-year breakdown
    currentCorpus = 0;
    yearIndex = 0;

    // Investment phase
    for (let year = 1; year <= investmentPeriod; year++) {
      const yearlyInvestment = monthlySIP * 12;
      currentCorpus = (currentCorpus + yearlyInvestment) * (1 + accumulationReturn / 100);
      
      yearlyBreakdown[yearIndex].investment = yearlyInvestment;
      yearlyBreakdown[yearIndex].corpus = currentCorpus;
      yearIndex++;
    }

    // Gap/Growth phase
    for (let year = 1; year <= gapPeriod; year++) {
      currentCorpus = currentCorpus * (1 + growthReturn / 100);
      
      yearlyBreakdown[yearIndex].corpus = currentCorpus;
      yearIndex++;
    }

    // SWP phase
    let withdrawalCorpus = currentCorpus;
    for (let year = 1; year <= swpDuration; year++) {
      const yearlyWithdrawal = totalAnnualRequirements[year - 1];
      const swpYear = yearsToSWPStart + year;
      const yearInflation = Math.pow(1 + inflationRate / 100, swpYear);

      // Calculate monthly income for this year
      let monthlyIncome = 0;
      if (year >= monthlyExpensesStartYear && year <= monthlyExpensesEndYear ||
          year >= emiStartYear && year <= emiEndYear) {
        monthlyIncome = futureDesiredIncome * Math.pow(1 + inflationRate / 100, year - 1);
        
        if (year >= monthlyExpensesStartYear && year <= monthlyExpensesEndYear) {
          monthlyIncome += monthlyExpenses * yearInflation;
        }
        if (year >= emiStartYear && year <= emiEndYear) {
          monthlyIncome += emiAmount * yearInflation;
        }
      } else {
        monthlyIncome = futureDesiredIncome * Math.pow(1 + inflationRate / 100, year - 1);
      }

      // Corpus grows and then withdrawal happens
      withdrawalCorpus = (withdrawalCorpus * (1 + withdrawalReturn / 100)) - yearlyWithdrawal;
      
      yearlyBreakdown[yearIndex] = {
        year: yearIndex + 1,
        age: yearIndex + 1,
        phase: 'SWP Phase',
        investment: 0,
        corpus: Math.max(0, withdrawalCorpus),
        withdrawal: yearlyWithdrawal,
        income: monthlyIncome
      };
      yearIndex++;
    }

    // Alternative: Step-up SIP (increase by 10% annually)
    const stepUpPercentage = 10;
    let stepUpCorpus = 0;
    let stepUpMonthlySIP = monthlySIP * 0.7; // Start with 70% of regular SIP
    
    for (let year = 1; year <= investmentPeriod; year++) {
      const yearlyInvestment = stepUpMonthlySIP * 12;
      stepUpCorpus = (stepUpCorpus + yearlyInvestment) * (1 + accumulationReturn / 100);
      stepUpMonthlySIP = stepUpMonthlySIP * (1 + stepUpPercentage / 100);
    }

    // Grow during gap period
    stepUpCorpus = stepUpCorpus * Math.pow(1 + growthReturn / 100, gapPeriod);

    // Calculate current and future values for each expense component
    const currentMonthlyRequirement = desiredMonthlyIncome + monthlyExpenses + emiAmount;
    const currentAnnualRequirement = (currentMonthlyRequirement * 12) + schoolFees + houseHoldExpenses + otherExpenses;

    return {
      // Required amounts
      monthlySIP: Math.round(monthlySIP),
      lumpsumRequired: Math.round(lumpsumRequired),
      totalSIPInvestment: Math.round(totalSIPInvestment),

      // Corpus values
      corpusAtEndOfInvestment: Math.round(corpusAtEndOfInvestment),
      corpusAtSWPStart: Math.round(corpusRequiredAtSWPStart),
      finalCorpus: Math.round(withdrawalCorpus),

      // Monthly/Annual requirements
      currentMonthlyRequirement,
      futureMonthlyRequirement: Math.round(futureDesiredIncome + (monthlyExpenses * inflationMultiplier) + (emiAmount * inflationMultiplier)),
      currentAnnualRequirement,
      futureAnnualRequirement: Math.round(totalAnnualRequirements[0] || 0),

      // Breakdown
      futureDesiredIncome: Math.round(futureDesiredIncome),
      futureMonthlyExpenses: Math.round(monthlyExpenses * inflationMultiplier),
      futureEMI: Math.round(emiAmount * inflationMultiplier),
      futureSchoolFees: Math.round(schoolFees * inflationMultiplier),
      futureHouseHoldExpenses: Math.round(houseHoldExpenses * inflationMultiplier),
      futureOtherExpenses: Math.round(otherExpenses * inflationMultiplier),

      // Timeline
      yearlyBreakdown,

      // Step-up SIP
      stepUpInitialSIP: Math.round(monthlySIP * 0.7),
      stepUpFinalSIP: Math.round(monthlySIP * 0.7 * Math.pow(1.1, investmentPeriod - 1)),
      stepUpCorpus: Math.round(stepUpCorpus),
      stepUpShortfall: Math.round(Math.max(0, corpusRequiredAtSWPStart - stepUpCorpus))
    };
  }, [formData]);

  // Chart: Corpus growth over time
  const corpusGrowthChart = {
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const data = calculations.yearlyBreakdown[params[0].dataIndex];
        return `
          <strong>Year ${data.year}</strong><br/>
          Phase: ${data.phase}<br/>
          Corpus: ${formatCurrency(data.corpus)}<br/>
          ${data.investment > 0 ? `Investment: ${formatCurrency(data.investment)}<br/>` : ''}
          ${data.withdrawal > 0 ? `Withdrawal: ${formatCurrency(data.withdrawal)}<br/>` : ''}
        `;
      }
    },
    legend: {
      data: ['Corpus Value', 'Annual Investment', 'Annual Withdrawal'],
      top: 25
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: calculations.yearlyBreakdown.map(item => item.year),
      name: 'Year'
    },
    yAxis: {
      type: 'value',
      name: 'Amount (₹)',
      axisLabel: {
        formatter: (value) => `₹${(value / 10000000).toFixed(1)}Cr`
      }
    },
    series: [
      {
        name: 'Corpus Value',
        type: 'line',
        data: calculations.yearlyBreakdown.map(item => item.corpus),
        itemStyle: { color: '#19b48d' },
        areaStyle: { color: 'rgba(25, 180, 141, 0.1)' },
        lineStyle: { width: 3 }
      },
      {
        name: 'Annual Investment',
        type: 'bar',
        data: calculations.yearlyBreakdown.map(item => item.investment),
        itemStyle: { color: '#3b82f6' }
      },
      {
        name: 'Annual Withdrawal',
        type: 'bar',
        data: calculations.yearlyBreakdown.map(item => item.withdrawal),
        itemStyle: { color: '#ef4444' }
      }
    ]
  };

  // Chart: Phase-wise breakdown
  const phaseBreakdownChart = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} years ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left'
    },
    series: [
      {
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          formatter: '{b}\n{c} years'
        },
        data: [
          { value: formData.investmentPeriod, name: 'Investment Phase', itemStyle: { color: '#3b82f6' } },
          { value: formData.gapPeriod, name: 'Growth Phase', itemStyle: { color: '#f59e0b' } },
          { value: formData.swpDuration, name: 'SWP Phase', itemStyle: { color: '#10b981' } }
        ]
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[95vh] overflow-y-auto my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-[#19b48d] flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="text-white text-sm" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              SWP Calculator - Future Income Planning
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Input Section */}
            <div className="lg:col-span-1 space-y-6">
              {/* Goal Settings */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Your Goal</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Desired Monthly Income (Today's Value) ₹
                    </label>
                    <input
                      type="number"
                      value={formData.desiredMonthlyIncome}
                      onChange={(e) => handleInputChange('desiredMonthlyIncome', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formatCurrency(formData.desiredMonthlyIncome)}/month
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SWP Duration (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.swpDuration}
                      onChange={(e) => handleInputChange('swpDuration', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                      min="1"
                      max="50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How long you need the income
                    </p>
                  </div>
                </div>
              </div>

              {/* Timeline Settings */}
              <div className="bg-blue-50 rounded-xl shadow-sm border border-blue-200 p-6">
                <h4 className="text-md font-semibold text-blue-900 mb-4">Timeline</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Investment Period (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.investmentPeriod}
                      onChange={(e) => handleInputChange('investmentPeriod', e.target.value)}
                      className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      min="1"
                      max="30"
                    />
                    <p className="text-xs text-blue-700 mt-1">
                      How long you will invest
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Gap Period (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.gapPeriod}
                      onChange={(e) => handleInputChange('gapPeriod', e.target.value)}
                      className="w-full px-4 py-3 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      min="0"
                      max="30"
                    />
                    <p className="text-xs text-blue-700 mt-1">
                      Years between investment stop and SWP start
                    </p>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-white rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Total Timeline:</strong> {formData.investmentPeriod + formData.gapPeriod + formData.swpDuration} years
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    SWP starts after {formData.investmentPeriod + formData.gapPeriod} years
                  </p>
                </div>
              </div>

              {/* Additional Expenses with Time Periods */}
              <div className="bg-orange-50 rounded-xl shadow-sm border border-orange-200 p-6">
                <h4 className="text-md font-semibold text-orange-900 mb-4">Additional Expenses (Today's Value)</h4>
                <div className="space-y-4">
                  {/* Monthly Household Expenses */}
                  <div className="p-3 bg-white rounded-lg border border-orange-200">
                    <label className="block text-sm font-medium text-orange-800 mb-2">
                      Monthly Household Expenses ₹
                    </label>
                    <input
                      type="number"
                      value={formData.monthlyExpenses}
                      onChange={(e) => handleInputChange('monthlyExpenses', e.target.value)}
                      className="w-full px-4 py-3 border border-orange-300 bg-white rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent mb-2"
                      min="0"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">Start Year</label>
                        <input
                          type="number"
                          value={formData.monthlyExpensesStartYear}
                          onChange={(e) => handleInputChange('monthlyExpensesStartYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">End Year</label>
                        <input
                          type="number"
                          value={formData.monthlyExpensesEndYear}
                          onChange={(e) => handleInputChange('monthlyExpensesEndYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Monthly EMI */}
                  <div className="p-3 bg-white rounded-lg border border-orange-200">
                    <label className="block text-sm font-medium text-orange-800 mb-2">
                      Monthly EMI (if any) ₹
                    </label>
                    <input
                      type="number"
                      value={formData.emiAmount}
                      onChange={(e) => handleInputChange('emiAmount', e.target.value)}
                      className="w-full px-4 py-3 border border-orange-300 bg-white rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent mb-2"
                      min="0"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">Start Year</label>
                        <input
                          type="number"
                          value={formData.emiStartYear}
                          onChange={(e) => handleInputChange('emiStartYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">End Year</label>
                        <input
                          type="number"
                          value={formData.emiEndYear}
                          onChange={(e) => handleInputChange('emiEndYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Annual School Fees */}
                  <div className="p-3 bg-white rounded-lg border border-orange-200">
                    <label className="block text-sm font-medium text-orange-800 mb-2">
                      Annual School Fees ₹
                    </label>
                    <input
                      type="number"
                      value={formData.schoolFees}
                      onChange={(e) => handleInputChange('schoolFees', e.target.value)}
                      className="w-full px-4 py-3 border border-orange-300 bg-white rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent mb-2"
                      min="0"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">Start Year</label>
                        <input
                          type="number"
                          value={formData.schoolFeesStartYear}
                          onChange={(e) => handleInputChange('schoolFeesStartYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">End Year</label>
                        <input
                          type="number"
                          value={formData.schoolFeesEndYear}
                          onChange={(e) => handleInputChange('schoolFeesEndYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Annual Household Expenses */}
                  <div className="p-3 bg-white rounded-lg border border-orange-200">
                    <label className="block text-sm font-medium text-orange-800 mb-2">
                      Annual Household Expenses ₹
                    </label>
                    <input
                      type="number"
                      value={formData.houseHoldExpenses}
                      onChange={(e) => handleInputChange('houseHoldExpenses', e.target.value)}
                      className="w-full px-4 py-3 border border-orange-300 bg-white rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent mb-2"
                      min="0"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">Start Year</label>
                        <input
                          type="number"
                          value={formData.houseHoldExpensesStartYear}
                          onChange={(e) => handleInputChange('houseHoldExpensesStartYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">End Year</label>
                        <input
                          type="number"
                          value={formData.houseHoldExpensesEndYear}
                          onChange={(e) => handleInputChange('houseHoldExpensesEndYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Others Section */}
                  <div className="p-3 bg-white rounded-lg border border-orange-200">
                    <label className="block text-sm font-medium text-orange-800 mb-2">
                      Other Expense Name
                    </label>
                    <input
                      type="text"
                      value={formData.otherExpenseName}
                      onChange={(e) => handleInputChange('otherExpenseName', e.target.value)}
                      className="w-full px-4 py-3 border border-orange-300 bg-white rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent mb-2"
                      placeholder="e.g., Travel, Medical, etc."
                    />
                    <label className="block text-sm font-medium text-orange-800 mb-2">
                      Annual Other Expenses ₹
                    </label>
                    <input
                      type="number"
                      value={formData.otherExpenses}
                      onChange={(e) => handleInputChange('otherExpenses', e.target.value)}
                      className="w-full px-4 py-3 border border-orange-300 bg-white rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent mb-2"
                      min="0"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">Start Year</label>
                        <input
                          type="number"
                          value={formData.otherExpensesStartYear}
                          onChange={(e) => handleInputChange('otherExpensesStartYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-orange-700 mb-1">End Year</label>
                        <input
                          type="number"
                          value={formData.otherExpensesEndYear}
                          onChange={(e) => handleInputChange('otherExpensesEndYear', e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 bg-white rounded-lg text-sm"
                          min="0"
                          max={formData.swpDuration}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return Assumptions */}
              <div className="bg-purple-50 rounded-xl shadow-sm border border-purple-200 p-6">
                <h4 className="text-md font-semibold text-purple-900 mb-4">Return Assumptions (%)</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-2">
                      Accumulation Phase Return
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.accumulationReturn}
                      onChange={(e) => handleInputChange('accumulationReturn', e.target.value)}
                      className="w-full px-4 py-3 border border-purple-300 bg-white rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                      min="0"
                      max="30"
                    />
                    <p className="text-xs text-purple-700 mt-1">During investment period</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-2">
                      Growth Phase Return
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.growthReturn}
                      onChange={(e) => handleInputChange('growthReturn', e.target.value)}
                      className="w-full px-4 py-3 border border-purple-300 bg-white rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                      min="0"
                      max="30"
                    />
                    <p className="text-xs text-purple-700 mt-1">During gap period</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-2">
                      SWP Phase Return
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.withdrawalReturn}
                      onChange={(e) => handleInputChange('withdrawalReturn', e.target.value)}
                      className="w-full px-4 py-3 border border-purple-300 bg-white rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                      min="0"
                      max="30"
                    />
                    <p className="text-xs text-purple-700 mt-1">During withdrawal period</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-2">
                      Inflation Rate
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.inflationRate}
                      onChange={(e) => handleInputChange('inflationRate', e.target.value)}
                      className="w-full px-4 py-3 border border-purple-300 bg-white rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                      min="0"
                      max="20"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* Key Results */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Monthly SIP Required */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-6">
                  <div className="text-center">
                    <SafeIcon icon={FiTrendingUp} className="text-blue-600 text-5xl mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-blue-900 mb-2">Required Monthly SIP</h4>
                    <p className="text-5xl font-bold text-blue-900 mb-2">
                      {formatCurrency(calculations.monthlySIP)}
                    </p>
                    <p className="text-sm text-blue-700">
                      for {formData.investmentPeriod} years
                    </p>
                  </div>

                  <div className="mt-6 pt-6 border-t border-blue-200">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-sm text-blue-700 mb-1">Total Investment</p>
                        <p className="text-xl font-bold text-blue-900">{formatCurrency(calculations.totalSIPInvestment)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700 mb-1">Corpus at End</p>
                        <p className="text-xl font-bold text-blue-900">{formatCurrency(calculations.corpusAtEndOfInvestment)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lumpsum Required */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-6">
                  <div className="text-center">
                    <SafeIcon icon={FiDollarSign} className="text-green-600 text-5xl mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-green-900 mb-2">Required Lumpsum</h4>
                    <p className="text-5xl font-bold text-green-900 mb-2">
                      {formatCurrency(calculations.lumpsumRequired)}
                    </p>
                    <p className="text-sm text-green-700">
                      one-time investment today
                    </p>
                  </div>

                  <div className="mt-6 pt-6 border-t border-green-200">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-sm text-green-700 mb-1">Grows to</p>
                        <p className="text-xl font-bold text-green-900">{formatCurrency(calculations.corpusAtEndOfInvestment)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-green-700 mb-1">In</p>
                        <p className="text-xl font-bold text-green-900">{formData.investmentPeriod} years</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Corpus Journey */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Corpus Journey</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <SafeIcon icon={FiClock} className="text-blue-600 text-2xl mx-auto mb-2" />
                    <p className="text-sm text-blue-700 mb-1">After Investment</p>
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(calculations.corpusAtEndOfInvestment)}</p>
                    <p className="text-xs text-blue-600 mt-1">Year {formData.investmentPeriod}</p>
                  </div>

                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <SafeIcon icon={FiTrendingUp} className="text-orange-600 text-2xl mx-auto mb-2" />
                    <p className="text-sm text-orange-700 mb-1">At SWP Start</p>
                    <p className="text-2xl font-bold text-orange-900">{formatCurrency(calculations.corpusAtSWPStart)}</p>
                    <p className="text-xs text-orange-600 mt-1">Year {formData.investmentPeriod + formData.gapPeriod}</p>
                  </div>

                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <SafeIcon icon={FiPieChart} className="text-green-600 text-2xl mx-auto mb-2" />
                    <p className="text-sm text-green-700 mb-1">Final Corpus</p>
                    <p className="text-2xl font-bold text-green-900">{formatCurrency(calculations.finalCorpus)}</p>
                    <p className="text-xs text-green-600 mt-1">Year {formData.investmentPeriod + formData.gapPeriod + formData.swpDuration}</p>
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Corpus Growth Over Time</h4>
                  <div className="h-80">
                    <ReactECharts option={corpusGrowthChart} style={{ height: '100%', width: '100%' }} />
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Timeline Breakdown</h4>
                  <div className="h-80">
                    <ReactECharts option={phaseBreakdownChart} style={{ height: '100%', width: '100%' }} />
                  </div>
                </div>
              </div>

              {/* Detailed Year-by-Year Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Year-by-Year Breakdown</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Year</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Phase</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Investment</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Corpus</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Withdrawal</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Monthly Income</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculations.yearlyBreakdown.map((row, index) => (
                        <tr
                          key={index}
                          className={`border-b border-gray-100 ${
                            row.phase === 'Investment Phase'
                              ? 'bg-blue-50'
                              : row.phase === 'Growth Phase'
                              ? 'bg-orange-50'
                              : 'bg-green-50'
                          }`}
                        >
                          <td className="py-3 px-4 font-medium text-gray-900">{row.year}</td>
                          <td className="py-3 px-4 text-gray-700">{row.phase}</td>
                          <td className="py-3 px-4 text-right text-gray-900">
                            {row.investment > 0 ? formatCurrency(row.investment) : '-'}
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">
                            {formatCurrency(row.corpus)}
                          </td>
                          <td className="py-3 px-4 text-right text-red-600">
                            {row.withdrawal > 0 ? formatCurrency(row.withdrawal) : '-'}
                          </td>
                          <td className="py-3 px-4 text-right text-green-700">
                            {row.income > 0 ? formatCurrency(row.income) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Key Insights */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Key Insights</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Investment Required:</strong> You need to invest {formatCurrency(calculations.monthlySIP)}/month for {formData.investmentPeriod} years, OR a one-time lumpsum of {formatCurrency(calculations.lumpsumRequired)} today.
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Growth Period:</strong> After stopping investments, your corpus will grow from {formatCurrency(calculations.corpusAtEndOfInvestment)} to {formatCurrency(calculations.corpusAtSWPStart)} over {formData.gapPeriod} years at {formData.growthReturn}% return.
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Time-Based Expenses:</strong> Your expenses are configured to start and stop at different times during the SWP phase, allowing for flexible cashflow management based on your life events.
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Final Corpus:</strong> At the end of {formData.swpDuration} years of withdrawals, you will have approximately {formatCurrency(calculations.finalCorpus)} remaining.
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Inflation Impact:</strong> All expenses are automatically adjusted for {formData.inflationRate}% inflation each year during the SWP phase.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default SWPCalculator;